import React, { useState } from 'react';
import { useEffectAsync } from '../../../helpers';
import useLoading from '../../Base/Hooks/useLoading';
import { CustomSettingKey } from '../../Base/Enums/CustomSettingKey.enum';
import useCustomSettings from '../../Base/Hooks/useCustomSettings';
import { useAppContext } from '../../../Services/AppContext';

export interface CustomSettingsContextProviderProps<TSettings> {
	children?: React.ReactNode | React.ReactNode[];
	customSettingsKey: CustomSettingKey;
}

export interface CustomSettingsContextProps<TSettings> {
	customSettings: TSettings | undefined;
	refetchCustomSettings: () => Promise<void>;
	isLoading: boolean;
}

const CustomSettingsContext = React.createContext<
	CustomSettingsContextProps<any> | undefined
>(undefined);

export const useCustomSettingsContext = <
	TSettings,
>(): CustomSettingsContextProps<TSettings> => {
	const ctx = React.useContext(CustomSettingsContext);

	if (!ctx) {
		return {
			customSettings: undefined,
			refetchCustomSettings: async () => {},
			isLoading: false,
		};
	}

	return ctx as CustomSettingsContextProps<TSettings>;
};

export function CustomSettingsContextProvider<TSettings>({
	children,
	customSettingsKey,
}: CustomSettingsContextProviderProps<TSettings>) {
	const { userData, workplaceId } = useAppContext();
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const { getCustomSettings } = useCustomSettings(workplaceId, userData.id);
	const [customSettings, setCustomSettings] = useState<TSettings | undefined>();

	const loadCustomSettings = async () => {
		setIsLoading(true);
		const settings = await getCustomSettings(customSettingsKey);
		if (settings) {
			setCustomSettings(settings as TSettings);
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await loadCustomSettings();
	}, []);

	const value: CustomSettingsContextProps<TSettings> = {
		customSettings,
		refetchCustomSettings: loadCustomSettings,
		isLoading,
	};

	return (
		<CustomSettingsContext.Provider value={value}>
			{isLoading ? <RenderLoading /> : children}
		</CustomSettingsContext.Provider>
	);
}
